"use client";

import { useState, type FormEvent } from "react";
import { Box, Button, CircularProgress, TextField, Typography } from "@mui/material";
import { portalFont, portalTokens } from "../partnerTheme";
import { requestPartnerAccess } from "../partnerAuth";

const GATE_THEME: Record<
  string,
  { accent: string; blurb: string; placeholder: string; eyebrow: string }
> = {
  "dr-squatch": {
    accent: "#2F7A3E",
    blurb:
      "Access is limited to Dr. Squatch and ComedyUO email addresses. We will email you a one-time link.",
    placeholder: "you@drsquatch.com",
    eyebrow: "Partner portal",
  },
  skrewball: {
    accent: "#C45C26",
    blurb:
      "Access is limited to Pernod Ricard and ComedyUO email addresses. We will email you a one-time link.",
    placeholder: "you@pernod-ricard.com",
    eyebrow: "Partner portal",
  },
  "draft-kings": {
    accent: "#72BB3B",
    blurb:
      "This is a private ComedyUO case study of The Taping, using our Dr. Squatch partnership as the example. Access is limited to approved email addresses.",
    placeholder: "you@company.com",
    eyebrow: "Case study",
  },
  "amex-esb": {
    accent: "#006FCF",
    blurb:
      "This partnership concept was built for American Express around the Empire State Residency. Access is limited to American Express and ComedyUO email addresses.",
    placeholder: "you@aexp.com",
    eyebrow: "American Express overview",
  },
};

const DEFAULT_THEME = {
  accent: "#2F7A3E",
  blurb:
    "Access is limited to this partner's work email and ComedyUO. We will email you a one-time link.",
  placeholder: "you@company.com",
  eyebrow: "Partner portal",
};

export default function PartnerAccessGate({
  slug,
  onDebugMagicUrl,
}: {
  slug: string;
  /** Local/dev only: parent can auto-open debug magic links. */
  onDebugMagicUrl?: (url: string) => void;
}) {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [sentTo, setSentTo] = useState<string | null>(null);
  const theme = GATE_THEME[slug] ?? DEFAULT_THEME;
  const buttonTextColor = slug === "draft-kings" ? "#0a0a0a" : "#fff";

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!email.trim() || sending) return;
    setSending(true);
    setError("");
    setSentTo(null);
    const result = await requestPartnerAccess(email.trim(), slug);
    setSending(false);
    if (!result.ok) {
      setError(result.error || "Could not send access email.");
      return;
    }
    setSentTo(email.trim());
    // Only auto-follow debug links when email was intentionally skipped.
    if (result.debugMagicUrl && result.debugEmailSkipped && onDebugMagicUrl) {
      onDebugMagicUrl(result.debugMagicUrl);
    }
  }

  return (
    <Box
      component="main"
      sx={{
        bgcolor: portalTokens.background,
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2.5,
        py: 8,
      }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          width: "100%",
          maxWidth: 420,
          p: { xs: 3, md: 4 },
          borderRadius: "24px",
          border: `1px solid color-mix(in srgb, ${theme.accent} 35%, transparent)`,
          bgcolor: "rgba(10,10,10,0.96)",
        }}
      >
        <Typography
          sx={{
            fontFamily: portalFont,
            fontSize: "0.75rem",
            fontWeight: 700,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            color: theme.accent,
            mb: 1.5,
          }}
        >
          {theme.eyebrow}
        </Typography>
        <Typography
          component="h1"
          sx={{
            fontFamily: portalFont,
            fontWeight: 700,
            color: "#fff",
            fontSize: { xs: "1.6rem", md: "1.85rem" },
            lineHeight: 1.15,
            letterSpacing: "-0.02em",
            mb: 1.25,
          }}
        >
          Sign in with your work email
        </Typography>
        <Typography
          sx={{
            fontFamily: portalFont,
            color: "rgba(244,245,247,0.72)",
            fontSize: "0.95rem",
            lineHeight: 1.5,
            mb: 3,
          }}
        >
          {theme.blurb}
        </Typography>

        {sentTo ? (
          <Box
            sx={{
              p: 2,
              borderRadius: "14px",
              bgcolor: `color-mix(in srgb, ${theme.accent} 16%, transparent)`,
              border: `1px solid color-mix(in srgb, ${theme.accent} 40%, transparent)`,
            }}
          >
            <Typography sx={{ fontFamily: portalFont, color: "#fff", fontWeight: 600, mb: 0.5 }}>
              Check your inbox
            </Typography>
            <Typography sx={{ fontFamily: portalFont, color: "rgba(244,245,247,0.78)", fontSize: "0.9rem" }}>
              We sent a link to {sentTo}. It expires in 30 minutes.
            </Typography>
          </Box>
        ) : (
          <>
            <TextField
              fullWidth
              type="email"
              required
              autoComplete="email"
              label="Work email"
              placeholder={theme.placeholder}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{
                mb: 2,
                "& .MuiInputBase-root": {
                  color: "#fff",
                  bgcolor: "rgba(255,255,255,0.04)",
                  alignItems: "center",
                  minHeight: 56,
                },
                "& .MuiInputBase-input": {
                  py: 1.75,
                  lineHeight: 1.4,
                  display: "flex",
                  alignItems: "center",
                  "&::placeholder": {
                    opacity: 0.55,
                    lineHeight: 1.4,
                    transform: "none",
                  },
                },
                "& .MuiInputLabel-root": { color: "rgba(244,245,247,0.65)" },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: `color-mix(in srgb, ${theme.accent} 40%, transparent)`,
                },
                "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: `${theme.accent} !important`,
                },
                "& .MuiInputLabel-root.Mui-focused": { color: theme.accent },
              }}
            />
            {error ? (
              <Typography
                sx={{
                  fontFamily: portalFont,
                  color: "#f87171",
                  fontSize: "0.875rem",
                  mb: 2,
                }}
              >
                {error}
              </Typography>
            ) : null}
            <Button
              type="submit"
              disabled={sending || !email.trim()}
              fullWidth
              sx={{
                fontFamily: portalFont,
                fontWeight: 700,
                textTransform: "none",
                bgcolor: theme.accent,
                color: buttonTextColor,
                borderRadius: "999px",
                py: 1.4,
                fontSize: "0.975rem",
                "&:hover": { bgcolor: theme.accent, filter: "brightness(1.08)" },
                "&.Mui-disabled": {
                  color:
                    slug === "draft-kings"
                      ? "rgba(10,10,10,0.45)"
                      : "rgba(255,255,255,0.5)",
                },
              }}
            >
              {sending ? (
                <CircularProgress size={22} sx={{ color: buttonTextColor }} />
              ) : (
                "Email me a link"
              )}
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
}
